<template>
  <div
    id="grecaptcha"
    ref="recaptcha"
    :class="[$style.recaptcha, 'g-recaptcha']"
    data-badge="inline"
    data-callback="onRecaptchaLoadCallback"
    :data-sitekey="sitekey"
    data-size="invisible"
  />
</template>

<script>
import { mapActions } from 'vuex';
import ConfigService from 'client/util/ConfigService';
import StoreConfigConstants from 'client/util/Constants/StoreConfigConstants';

const { recaptchaState } = StoreConfigConstants;

export default {
  name: 'RecaptchaInvisible',

  props: {
    value: {
      type: String,
      default: '',
    },
  },

  computed: {
    sitekey() {
      /**
       * These reCAPTCHA site keys are paired with a secret key that is stored in the SI repo
       * The local site key is a generic test key provided by Google that disables the reCAPTCHA.
       * This key is pulled from workstation ui-settings.json (local)
       * or from ui-settings (staging/production) managed in AdminPortal app.yaml
       */
      return ConfigService.signupInvisibleRecaptchaSiteKey;
    },
  },

  mounted() {
    if (window) this.loadScript();
    this.setRecaptchaLoaded(recaptchaState.NOT_LOADED);
  },

  methods: {
    ...mapActions(['setRecaptchaLoaded', 'setRecaptchaExpired']),
    loadScript() {
      /**
       * Define callback function to be called once reCAPTCHA is loaded.
       * Note that the name of this function is hard-coded in the third party JS to be named
       * `window.onRecaptchaLoadCallback`.
       */
      window.onRecaptchaLoadCallback = () => {
        this.render();
        window.onRecaptchaLoadCallback = null;
      };

      // Load Google Recaptcha script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      script.src = ConfigService.signupRecaptchaScriptUrl;

      const first = document.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(script, first);
    },

    render() {
      // Render the reCAPTCHA
      if (window && window.grecaptcha) {
        window.grecaptcha.render(this.$refs.recaptcha, {
          sitekey: this.sitekey,
          size: 'invisible',
          callback: (token) => {
            // Executed when the user submits a successful response
            this.setRecaptchaLoaded(recaptchaState.DONE_LOADING);
            this.$emit('input', token);
          },
          'expired-callback': () => {
            // Executed when the reCAPTCHA response expires and the user needs to re-verify
            this.$emit('input', '');
            window.grecaptcha.reset();
            this.setRecaptchaExpired(true);
          },
          'error-callback': () => {
            // Executed when reCAPTCHA encounters an error (usually network connectivity)
            this.setRecaptchaLoaded(recaptchaState.ERROR_LOADING);
            this.$emit('input', '');
          },
        });
      }
    },
  },
};
</script>

<style module>
  .recaptcha {
    bottom: 0;
    margin-bottom: 1rem;
  }
</style>

<style>
.grecaptcha-badge {
  visibility: hidden;
}
</style>
